// components/ui/TabGroup.tsx - Reusable tab component for navigation and filtering
import { SpinnerIcon } from "@/components/icons";
import { BRAND_GRADIENT_TRACK } from "@/components/ui/brandGradient";
import React, { ReactNode } from "react";

export interface TabItem<T = string> {
  id: T;
  label: string;
  // Icon variants
  icon?: ReactNode;
  iconType?: 'svg' | 'emoji' | 'component';

  // Badge/count display
  badge?: string | number;
  badgeVariant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';

  // Status indicator (small dot)
  statusPip?: boolean;
  statusColor?: 'green' | 'red' | 'yellow' | 'blue' | 'gray';

  // Disabled state
  disabled?: boolean;

  // Tooltip
  tooltip?: string;

  // Additional classes
  className?: string;
}

export interface TabGroupConfig<T = string> {
  variant: 'navigation' | 'filter' | 'pills' | 'underline';
  size: 'sm' | 'md' | 'lg';
  layout: 'horizontal' | 'grid';

  // Grid layout options (only applies when layout = 'grid')
  gridCols?: 2 | 3 | 4 | 5 | 6;
  gridColsSm?: 2 | 3 | 4 | 5 | 6;
  gridColsMd?: 2 | 3 | 4 | 5 | 6;

  // Container styling
  containerClassName?: string;
  fullWidth?: boolean;

  // Background container for tab group
  hasBackground?: boolean;
  backgroundClassName?: string;

  // Optional class applied to each tab label span (e.g., "hidden xl:inline" for responsive icon-only)
  labelClassName?: string;
}

interface TabGroupProps<T = string> {
  items: TabItem<T>[];
  activeId: T | null;
  onTabChange: (id: T) => void;
  config: TabGroupConfig<T>;

  // Loading state
  isLoading?: boolean;
  loadingCount?: number;

  className?: string;
}

export const TabGroup = <T extends string | number>({
  items,
  activeId,
  onTabChange,
  config,
  isLoading = false,
  loadingCount = 5,
  className = "",
}: TabGroupProps<T>) => {
  // Get base tab styling based on variant and size
  const getTabBaseClasses = (): string => {
    const sizeClasses = {
      sm: "px-2 py-1.5 text-xs",
      md: "px-3 py-2 text-sm",
      lg: "px-4 py-3 text-base"
    };

    const baseClasses = `
      inline-flex items-center justify-center font-medium transition-all duration-200
      cursor-pointer disabled:cursor-not-allowed disabled:opacity-50
      ${sizeClasses[config.size]}
    `;

    return baseClasses.trim();
  };

  // Get variant-specific styling
  const getVariantClasses = (isActive: boolean): string => {
    switch (config.variant) {
      case 'navigation':
        // Active pill is glass — translucent white over the gradient wash
        // so the track tint shows through (DD-012).
        if (isActive) return "bg-white/60 backdrop-blur-sm text-gray-900 shadow-xs rounded-md";
        return config.hasBackground
          ? "text-gray-600 hover:text-gray-900 hover:bg-white/30 rounded-md"
          : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md";

      case 'filter':
        return isActive
          ? "bg-blue-50 border-blue-200 text-blue-700 rounded-md border"
          : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 rounded-md border";

      case 'pills':
        return isActive
          ? "bg-blue-600 text-white rounded-full"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-full";

      case 'underline':
        return isActive
          ? "text-blue-600 border-b-2 border-blue-600"
          : "text-gray-500 hover:text-gray-700 border-b-2 border-transparent";

      default:
        return "";
    }
  };

  // Get container classes
  const getContainerClasses = (): string => {
    let classes = "";

    // Layout classes
    if (config.layout === 'grid') {
      classes += "grid gap-1";

      // Grid columns
      if (config.gridCols) classes += ` grid-cols-${config.gridCols}`;
      if (config.gridColsSm) classes += ` sm:grid-cols-${config.gridColsSm}`;
      if (config.gridColsMd) classes += ` md:grid-cols-${config.gridColsMd}`;
    } else {
      classes += "flex gap-1";
    }

    // Full width
    if (config.fullWidth) {
      classes += " w-full";
    }

    return classes;
  };

  // Get background container classes
  const getBackgroundClasses = (): string => {
    if (!config.hasBackground) return "";

    // Brand gradient track (DD-012) — the single default; call sites should
    // not pass their own backgroundClassName unless they need to deviate.
    return config.backgroundClassName || `${BRAND_GRADIENT_TRACK} rounded-lg p-1`;
  };

  // Render loading state — one small spinner, no pulsing pills (DD-001)
  if (isLoading) {
    return (
      <div className={`${getBackgroundClasses()} ${className}`}>
        <div className={`${getContainerClasses()} items-center justify-center py-1.5`}>
          <SpinnerIcon className="w-4 h-4 text-gray-400" />
        </div>
      </div>
    );
  }

  // Render badge — counts are a plain bold number inheriting the tab's
  // text color, so intensity follows selected/unselected state. Colored
  // pill variants stay for semantic badges (warnings etc.).
  const renderBadge = (badge: string | number, variant: string = 'default') => {
    const variantClasses = {
      primary: "px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700",
      success: "px-1.5 py-0.5 rounded-full bg-green-100 text-green-700",
      warning: "px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700",
      danger: "px-1.5 py-0.5 rounded-full bg-red-100 text-red-700"
    };
    const variantClass =
      variantClasses[variant as keyof typeof variantClasses] || "font-bold";

    return (
      <span className={`ml-2 text-xs shrink-0 ${variantClass}`}>
        {badge}
      </span>
    );
  };

  // Render status pip
  const renderStatusPip = (color: string = 'gray') => {
    const colorClasses = {
      green: "bg-green-500",
      red: "bg-red-500",
      yellow: "bg-yellow-500",
      blue: "bg-blue-500",
      gray: "bg-gray-500"
    };

    return (
      <div className={`
        w-2 h-2 rounded-full mr-2 shrink-0
        ${colorClasses[color as keyof typeof colorClasses] || colorClasses.gray}
      `} />
    );
  };

  // Render icon
  const renderIcon = (item: TabItem<T>) => {
    if (!item.icon) return null;

    return (
      <span className="mr-2 shrink-0">
        {item.icon}
      </span>
    );
  };

  const backgroundClasses = getBackgroundClasses();
  const containerClasses = getContainerClasses();

  return (
    <div className={`${backgroundClasses} ${className}`}>
      <div className={containerClasses}>
        {items.map((item) => {
          const isActive = activeId === item.id;

          return (
            <button
              key={String(item.id)}
              onClick={() => !item.disabled && onTabChange(item.id)}
              disabled={item.disabled}
              title={item.tooltip}
              aria-current={isActive ? "true" : undefined}
              className={`
                ${getTabBaseClasses()}
                ${getVariantClasses(isActive)}
                ${item.className || ""}
                min-w-0
              `}
            >
              {item.statusPip && renderStatusPip(item.statusColor)}
              {renderIcon(item)}
              <span className={`truncate ${config.labelClassName || ""}`}>{item.label}</span>
              {item.badge !== undefined && renderBadge(item.badge, item.badgeVariant)}
            </button>
          );
        })}
      </div>
    </div>
  );
};